import { useEffect, useState } from 'react'
import mqtt from "mqtt"

const MQTT_URL = "wss://mqtt.wecoenergy.com:8884"

/**
 * Custom hook to connect to MQTT broker
 * @returns {mqtt.Client} client - MQTT client
 * @returns {string} status - connection status
 */
export function useMQTTConnect(credentails, onMessage) {
  const [client, setClient] = useState(null)
  const [status, setStatus] = useState("")

  useEffect(() => {
    if (!credentails)
      return

    setStatus(`Connecting...`)
    const client = mqtt.connect(MQTT_URL, {
      username: credentails?.username,
      password: credentails?.password,
      reconnectPeriod: 2000,
      keepalive: 10,
    })
    setClient(client)

    return () => {
      client?.end(true)
    }
  }, [credentails])

  useEffect(() => {
    if (!client)
      return

    const handleConnect = () => {
      console.log("MQTT connected")
      client.subscribe('weco/#', (err) => {
        if (err) {
          console.error('Failed to subscribe to weco/#', err)
          setStatus("MQTT subscribe error")
        }
        else {
          setStatus("Connected")
        }
      })
    }

    const handleMessage = async (topic, message) => {
      // Telemetry is published as CBOR, which we don't decode here
      if (topic.endsWith('/telemetry'))
        return

      try {
        const payload = JSON.parse(message)
        Promise.resolve(onMessage(topic, payload)).catch(err =>
          console.error("onMessage error:", err)
        )
      } catch (e) {
        console.error("Invalid JSON:", e, message.toString())
      }
    }

    const handleDisconnect = () => {
      console.log("MQTT disconnected")
    }

    const handleError = (err) => {
      console.error('MQTT connection error:', err)
      setStatus("MQTT connection error")
    }

    const handleReconnect = () => {
      console.log("MQTT reconnecting")
      setStatus("Reconnecting...")
    }

    const handleOffline = () => {
      console.log("MQTT offline")
      setStatus("MQTT disconnected")
    }

    client.on('connect', handleConnect)
    client.on('message', handleMessage)
    client.on('disconnect', handleDisconnect)
    client.on('error', handleError)
    client.on('reconnect', handleReconnect)
    client.on('offline', handleOffline)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (client && !client.connected && !client.reconnecting && typeof client.reconnect === 'function') {
          console.log('Visibility change -> forcing MQTT reconnect')
          try {
            client.reconnect()
          }
          catch (e) {
            console.error('MQTT reconnect error', e)
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      try {
        client.off('connect', handleConnect)
        client.off('message', handleMessage)
        client.off('disconnect', handleDisconnect)
        client.off('error', handleError)
        client.off('reconnect', handleReconnect)
        client.off('offline', handleOffline)
      } catch (e) { }

      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [client, onMessage])

  return {client, status}
}

/**
 * MQTT subscribe and publish functions
 * @param {mqtt.Client} client - MQTT client
 * @param {string} topic - topic to subscribe to
 * @param {function} onMessage - callback function to handle incoming messages
 */
export function useMQTTSubscribe(client, topic, onMessage) {
  useEffect(() => {
    if (!client || !topic) return

    const handleMsg = function (receivedTopic, message) {
      if (receivedTopic === topic) {
        onMessage(message.toString())
      }
    }

    // `client.connected` flips by mutation, which React cannot observe, so
    // subscribe from the connect event; that also re-arms after a reconnect.
    const subscribe = () => {
      console.log(`Subscribing to ${topic}`)
      client.subscribe(topic)
    }

    client.on('message', handleMsg)
    client.on('connect', subscribe)
    if (client.connected)
      subscribe()

    return () => {
      console.log(`Unsubscribing from ${topic}`)
      client.off('connect', subscribe)
      client.off('message', handleMsg)
      if (client.connected)
        client.unsubscribe(topic)
    }
  }, [client, topic, onMessage])
}

/**
 * MQTT publish function
 * @param {mqtt.Client} client - MQTT client
 * @returns {function} publish - function to publish messages
 */
export function useMQTTPublish(client) {
  return function (topic, message, options = {}, callback = null) {
    if (client && client.connected) {
      client.publish(topic, message, options, callback)
    }
    else if (callback) {
      callback(new Error("MQTT client not connected"))
    }
  }
}